import React from 'react';
import { useNavigate } from 'react-router-dom';

import ekoImage from '../assets/eko-_transparent-removebg-preview.png';
import backdrop from '../assets/landing/backgroundupdated.png';
import logo from '../assets/20.png';

import './LandingPage.css';

// -------------------- PAGE --------------------
export const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => navigate('/login');

  return (
    <div className="dpp-landing">
      {/* App meadow backdrop */}
      <div
        className="scene"
        aria-hidden="true"
        style={{ backgroundImage: `url(${backdrop})` }}
      />

      {/* ============ NAV ============ */}
      <nav>
        <div className="wrap nav-inner">
          <div className="brand">
            <img src={logo} alt="The Dear Parent Project" className="brand-logo" />
          </div>
          <div className="nav-links">
            <a href="#windows">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor"><rect x="3" y="3" width="6" height="6" rx="1.6" strokeWidth="1.7" /><rect x="11" y="3" width="6" height="6" rx="1.6" strokeWidth="1.7" /><rect x="3" y="11" width="6" height="6" rx="1.6" strokeWidth="1.7" /><rect x="11" y="11" width="6" height="6" rx="1.6" strokeWidth="1.7" /></svg>
              What it shows
            </a>
            <a href="#before">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor"><circle cx="10" cy="10" r="7.5" strokeWidth="1.7" /><path d="M8 6.8 13 10 8 13.2V6.8Z" fill="currentColor" strokeLinejoin="round" strokeWidth="1.2" /></svg>
              Before you begin
            </a>
            <a href="/about">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor"><path d="M10 16.5C4 12.4 2.6 9.5 2.6 7.1 2.6 4.8 4.4 3.4 6.3 3.4 7.9 3.4 9.2 4.3 10 5.6 10.8 4.3 12.1 3.4 13.7 3.4 15.6 3.4 17.4 4.8 17.4 7.1 17.4 9.5 16 12.4 10 16.5Z" strokeWidth="1.7" strokeLinejoin="round" /></svg>
              About us
            </a>
            <button className="btn honey big" onClick={handleGetStarted}>Get Started</button>
          </div>
        </div>
      </nav>

      {/* ============ HERO / LETTER ============ */}
      <header className="hero">
        <div className="wrap">
          <div className="letter rise">
            <div className="eko-peek">
              <img src={ekoImage} alt="Eko, a friendly hedgehog holding a book" />
            </div>

            <div className="stamp" aria-hidden="true">
              <span className="heart">
                <svg width="26" height="24" viewBox="0 0 26 24" fill="none">
                  <path d="M13 22C4 15.5 2 11 2 7.5 2 4 4.7 2 7.5 2 9.8 2 11.8 3.3 13 5.3 14.2 3.3 16.2 2 18.5 2 21.3 2 24 4 24 7.5 24 11 22 15.5 13 22Z" fill="#C15A78" />
                </svg>
              </span>
              <span>Curious<br />Parents</span>
            </div>

            <div className="kicker">A note from Eko</div>
            <h1>Dear <span className="u">Parent</span>,</h1>
            <p>You have watched them figure out the world since day one the bedtime questions, the way they light up when something finally clicks. You know their heart by now. What is harder to see is <em>how</em> their mind works: the quiet patterns in how they read, wonder, and puzzle things out.</p>
            <p>That's the part I love just a few short, playful moments together, and a clearer picture of the learner they already are.</p>
            <p>Whenever you are ready, I will be right here&nbsp;&nbsp;-&nbsp;book in paw.</p>
            <div className="sign">Eko</div>

            <div className="hero-cta">
              <button className="btn honey big" onClick={handleGetStarted}>Get Started</button>
              <a href="#before" className="textlink">Before you begin</a>
            </div>
            <div className="fine">About 20–30 minutes, together. </div>
          </div>
        </div>
      </header>

      {/* ============ THESIS ============ */}
      <section className="thesis">
        <div className="wrap">
          <div className="panel">
            <div className="dots" aria-hidden="true">
              <i style={{ background: '#E0A032' }} />
              <i style={{ background: '#2E9187' }} />
              <i style={{ background: '#EC7A59' }} />
              <i style={{ background: '#5B7CC4' }} />
            </div>
            <h2>Every child perceives the world a little <span className="hl">differently</span>.</h2>
          </div>
        </div>
      </section>

      {/* ============ WINDOWS ============ */}
      <section id="windows">
        <div className="wrap">
          <div className="panel">
            <div className="sec-head" style={{ marginBottom: 10 }}>
              <span className="kicker">The Four Activities</span>
              <h2>Four playful ways to see how they think</h2>
            </div>
            <div className="windows">

              <div className="win">
                <div className="ic c1">
                  <img src="/assets/journey/word-wizard.png" alt="Word Wizard" className="activity-logo" />
                </div>
                <h3>Word Wizard</h3>
                <p>Spelling patterns and phonics.</p>
              </div>

              <div className="win">
                <div className="ic c3">
                  <img src="/assets/journey/read-aloud.png" alt="Read Aloud" className="activity-logo" />
                </div>
                <h3>Read Aloud</h3>
                <p>Reading aloud and explaining ideas.</p>
              </div>

              <div className="win">
                <div className="ic c2">
                  <img src="/assets/journey/story-explorer.png" alt="Story Explorer" className="activity-logo" />
                </div>
                <h3>Story Explorer</h3>
                <p>Understanding stories and written language.</p>
              </div>

              <div className="win">
                <div className="ic c4">
                  <img src="/assets/journey/logic-quest.png" alt="Logic Quest" className="activity-logo" />
                </div>
                <h3>Logic Quest</h3>
                <p>Problem solving and patterns.</p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ============ BEFORE YOU BEGIN ============ */}
      <section id="before" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="how">
            <div className="sec-head" style={{ marginBottom: 36 }}>
              <span className="kicker">Before you begin</span>
              <h2>A few small things that help</h2>
              <p>They support your child to do their best and give you the clearest picture.</p>
            </div>
            <div className="wrap">
              <div className="steps prep">
                <div className="step">
                  <div className="num n1">1</div>
                  <h3>Set the scene</h3>
                  <ul className="prep-list">
                    <li>Find a quiet spot where you won't be interrupted for about 20–30 minutes.</li>
                    <li>Pick a time when your child is calm and rested, mornings or after a snack often work best, not right before bed.</li>
                    <li>Have headphones ready if you can some activities use sound.</li>
                    <li>Check your device is charged and the volume is on.</li>
                    <li>Sit at a table or steady surface it makes tapping and typing easier.</li>
                  </ul>
                </div>
                <div className="step">
                  <div className="num n2">2</div>
                  <h3>Settle your child</h3>
                  <ul className="prep-list">
                    <li>Tell them it's a set of fun activities, not a test  nothing to get wrong.</li>
                    <li>Reassure them it's okay to take their time, and okay to guess.</li>
                    <li>Let them do it themselves the activities work best as their own responses.</li>
                    <li>Sit nearby for comfort if you like, just try not to point at answers.</li>
                  </ul>
                </div>
                <div className="step">
                  <div className="num n3">3</div>
                  <h3>During the activities</h3>
                  <ul className="prep-list">
                    <li>If your child gets tired, it's fine to pause and come back progress is saved.</li>
                    <li>Let them use the audio and replay buttons as much as they like.</li>
                    <li>Keep it light a little encouragement goes a long way.</li>
                  </ul>
                </div>
              </div>
              <div className="prep-reminder">
                <p><strong>A gentle reminder:</strong> this is a starting point for understanding how your child learns not a test they can pass or fail. Every child is different, and that's exactly the point.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PROMISE ============ */}
      <section id="promise" style={{ paddingTop: 16 }}>
        <div className="wrap">
          <div className="promise">
            <svg width="42" height="42" viewBox="0 0 46 46" fill="none">
              <path d="M23 5l14 6v10c0 11-7 17-14 20-7-3-14-9-14-20V11l14-6Z" fill="#FCF7EC" stroke="#22423A" strokeWidth="2.2" strokeLinejoin="round" />
              <path d="M16 23l5 5 10-11" stroke="#E0A032" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p><strong>A promise from Eko:</strong> I'm a starting point, not a verdict. No little activity decides who your child is  you and their teachers still know them best. I'm just here to help you notice.</p>
          </div>
        </div>
      </section>


      {/* ============ FOOTER ============ */}
      <footer id="contact">
        <div className="wrap foot-inner">
          <div className="brand" style={{ fontSize: 16 }}>
            <img src={logo} alt="The Dear Parent Project" className="brand-logo" />
          </div>
          <div>
            <a href="#windows">What it shows</a>
            <a href="#before">Before you begin</a>
            <a href="#promise">Our promise</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
